import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Download } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const STATUS_LABELS = { pending: "Pending with HOD", hod_approved: "HOD approved · awaiting admin" };

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={
    status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
    : status === "hod_approved" ? "bg-blue-50 text-[#002FA7] border-blue-200"
    : "bg-amber-50 text-amber-700 border-amber-200"}>
    {STATUS_LABELS[status] || status}
  </Badge>
);

export default function TeacherLeave() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ leave_type_id: "", from_date: "", to_date: "", duration: "", reason: "", emergency: false });
  const [busy, setBusy] = useState(false);

  const { data: cards = [] } = useQuery({
    queryKey: ["leave-dashboard"],
    queryFn: async () => (await api.get("/leave/dashboard")).data,
  });
  const { data: mine = [] } = useQuery({
    queryKey: ["leave-my"],
    queryFn: async () => (await api.get("/leave/my")).data,
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leave/apply", { ...form, duration: Number(form.duration) });
      toast.success("Leave application submitted");
      setForm({ leave_type_id: "", from_date: "", to_date: "", duration: "", reason: "", emergency: false });
      queryClient.invalidateQueries({ queryKey: ["leave-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["leave-my"] });
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Leave">
      <div className="bg-white border border-slate-200 rounded-md mb-8" data-testid="leave-dashboard-card">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Leave dashboard</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="leave-download-excel"
              onClick={() => downloadFile("/leave/my-report.xlsx", "my_leave_report.xlsx")}>
              <Download className="h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="leave-download-pdf"
              onClick={() => downloadFile("/leave/my-report.pdf", "my_leave_report.pdf")}>
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Leave type</TableHead>
              <TableHead className="text-right">Brought forward</TableHead>
              <TableHead className="text-right">Earned</TableHead>
              <TableHead className="text-right">Availed</TableHead>
              <TableHead className="text-right">Late penalty</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">To carry forward</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cards.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8" data-testid="leave-dashboard-empty">
                No leave categories configured yet. Ask the admin to set them up.
              </TableCell></TableRow>
            )}
            {cards.map((c) => (
              <TableRow key={c.leave_type_id} data-testid={`leave-card-${c.leave_type_id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-right font-data">{c.brought_forward}</TableCell>
                <TableCell className="text-right font-data">{c.earned}</TableCell>
                <TableCell className="text-right font-data">{c.availed}</TableCell>
                <TableCell className="text-right font-data text-amber-700">{c.penalty_days || 0}</TableCell>
                <TableCell className="text-right font-data font-semibold" data-testid={`leave-balance-${c.leave_type_id}`}>{c.balance}</TableCell>
                <TableCell className="text-right font-data">{c.carry_forward ? c.to_carry_forward : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="leave-apply-card">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950 mb-4">Apply for leave</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Leave type</Label>
              <Select value={form.leave_type_id} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Select leave type" /></SelectTrigger>
                <SelectContent>
                  {cards.map((c) => (
                    <SelectItem key={c.leave_type_id} value={c.leave_type_id} data-testid={`leave-type-${c.leave_type_id}`}>
                      {c.name} (balance: {c.balance})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>From date</Label>
                <Input type="date" required data-testid="leave-from-input" value={form.from_date}
                  onChange={(e) => setForm({ ...form, from_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>To date</Label>
                <Input type="date" required data-testid="leave-to-input" value={form.to_date}
                  onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Duration (days, 0.5 = half day)</Label>
              <Input type="number" step="0.5" min="0.5" required data-testid="leave-duration-input"
                value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea required rows={3} data-testid="leave-reason-input" value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.emergency} data-testid="leave-emergency-toggle"
                onCheckedChange={(v) => setForm({ ...form, emergency: v })} />
              <Label className="cursor-pointer">Emergency leave (overrides application deadline)</Label>
            </div>
            <Button type="submit" disabled={busy} data-testid="leave-submit-btn"
              className="w-full bg-slate-950 hover:bg-slate-800 text-white">
              <Send className="h-4 w-4" /> {busy ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="leave-my-card">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">My applications</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="text-right">Days</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mine.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8" data-testid="leave-my-empty">
                  No applications yet.
                </TableCell></TableRow>
              )}
              {mine.map((a) => (
                <TableRow key={a.id} data-testid={`leave-my-${a.id}`}>
                  <TableCell className="font-medium">{a.leave_type_name}{a.emergency ? " (E)" : ""}</TableCell>
                  <TableCell className="font-data text-sm">{a.from_date} → {a.to_date}</TableCell>
                  <TableCell className="text-right font-data">{a.duration}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
